'use client';

import { useState, useTransition } from 'react';
import { validateDiscountCode } from '@/features/totems/presentation/actions/web-totem/validate-discount-code';
import type { useTotemCart } from '@/features/totems/presentation/hooks/web-totem/use-totem-cart';

type TotemCart = ReturnType<typeof useTotemCart>;

interface UseDiscountCodeOptions {
  locationId: string | null | undefined;
  cart: Pick<TotemCart, 'subtotal' | 'applyDiscount'>;
}

/** State and submit handler for the cart discount-code field (shared by the totem panel, mobile drawer and carta bar). */
export function useDiscountCode({ locationId, cart }: UseDiscountCodeOptions) {
  const [discountCode, setDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [isPending, startTransition] = useTransition();

  const changeDiscountCode = (value: string) => {
    setDiscountCode(value);
    setDiscountError('');
  };

  const applyDiscount = () => {
    if (!discountCode.trim() || !locationId) return;

    setDiscountError('');
    startTransition(async () => {
      const result = await validateDiscountCode(discountCode.trim(), locationId, cart.subtotal);
      if (result.success && result.discount) {
        cart.applyDiscount({
          id: result.discount.id,
          code: result.discount.code,
          amount: result.discount.amount,
          type: result.discount.type === 'percentage' ? 'percentage' : 'fixed',
          method: result.discount.method,
          minPurchase: result.discount.minPurchase,
        });
        setDiscountCode('');
      } else {
        setDiscountError(result.message || 'Código inválido');
      }
    });
  };

  return { discountCode, discountError, isPending, changeDiscountCode, applyDiscount };
}
